import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAppSelector } from '../store/hooks';

export interface LevelOption {
  value: string;
  label: string;
}

const STORAGE_KEY = 'clara.levels.v1';
const TTL_MS = 24 * 60 * 60 * 1000; // 24h

interface CacheEntry {
  ts: number;
  levels: LevelOption[];
}

// In-memory cache, shared across hook instances within a session.
let cached: LevelOption[] | null = null;

function readStored(): LevelOption[] | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CacheEntry;
    if (!parsed || !Array.isArray(parsed.levels) || typeof parsed.ts !== 'number') return null;
    if (Date.now() - parsed.ts > TTL_MS) return null; // expired
    return parsed.levels;
  } catch {
    return null;
  }
}

function writeStored(levels: LevelOption[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ts: Date.now(), levels }));
  } catch {
    // ignore quota / unavailable storage
  }
}

export function useLevels(): LevelOption[] {
  // Seed from the in-memory cache, then from localStorage — so a page reload
  // within 24h skips the network call entirely.
  const [levels, setLevels] = useState<LevelOption[]>(() => {
    if (cached === null) cached = readStored();
    return cached || [];
  });
  const token = useAppSelector(
    (state) => (state as { auth?: { token?: string | null } }).auth?.token,
  );

  useEffect(() => {
    if (cached && cached.length > 0) return; // fresh cache hit — no fetch
    const API_BASE_URL = import.meta.env.VITE_API_URL as string;
    axios
      .get(`${API_BASE_URL}/levels`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      .then((res) => {
        cached = res.data.levels;
        writeStored(cached!);
        setLevels(cached!);
      })
      .catch(() => {});
  }, [token]);

  return levels;
}
